import { useState } from 'react';
import { UserPlus, UserMinus, Crown } from 'lucide-react';
import { useEntityMembers, useAddMember, useRemoveMember } from '../hooks/useEntityMembers.js';
import { useAdminUsers } from '../hooks/useAdminUsers.js';

/**
 * Inline "Partage" section for an entity card (EntitiesAdminPage) — lists
 * members, lets an admin add a user (picker excludes existing members) or
 * revoke access. Role selection is intentionally not exposed (YAGNI — new
 * shares default to 'admin' server-side).
 */
export default function EntitySharePanel({ entityId, entityName }) {
  const { data: members, isLoading, error } = useEntityMembers(entityId);
  const { data: users } = useAdminUsers();
  const addMember = useAddMember(entityId);
  const removeMember = useRemoveMember(entityId);
  const [pickedUserId, setPickedUserId] = useState('');

  const memberIds = new Set((members ?? []).map((m) => m.user_id));
  const eligibleUsers = (users ?? []).filter((u) => !memberIds.has(u.id));

  async function handleAdd(e) {
    e.preventDefault();
    if (!pickedUserId) return;
    try {
      await addMember.mutateAsync(pickedUserId);
      setPickedUserId('');
    } catch {
      // error surfaced below via addMember.error
    }
  }

  async function handleRemove(member) {
    if (member.is_owner) return;
    const label = member.display_name || member.email;
    if (!window.confirm(`Retirer l'accès de ${label} à "${entityName}" ?`)) return;
    try {
      await removeMember.mutateAsync(member.user_id);
    } catch {
      // error surfaced below via removeMember.error
    }
  }

  return (
    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Partage</h3>

      {isLoading && <p className="mt-2 text-xs text-slate-500">Chargement...</p>}
      {error && (
        <p className="mt-2 text-xs text-red-600" role="alert">
          {error.message}
        </p>
      )}

      {!isLoading && !error && (
        <ul className="mt-2 divide-y divide-slate-200">
          {(members ?? []).length === 0 && (
            <li className="py-2 text-xs text-slate-500">Aucun membre.</li>
          )}
          {(members ?? []).map((m) => (
            <li key={m.user_id} className="flex items-center justify-between gap-2 py-2">
              <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-1.5 truncate text-sm text-slate-800">
                  <span className="truncate">{m.display_name || m.email}</span>
                  {m.is_owner && (
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                      <Crown size={10} aria-hidden />
                      Propriétaire
                    </span>
                  )}
                </p>
                {m.display_name && <p className="truncate text-xs text-slate-500">{m.email}</p>}
              </div>
              <button
                type="button"
                onClick={() => handleRemove(m)}
                disabled={m.is_owner || removeMember.isPending}
                className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-red-200 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label={`Retirer ${m.display_name || m.email} de ${entityName}`}
              >
                <UserMinus size={12} aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}

      {removeMember.error && (
        <p className="mt-1 text-xs text-red-600" role="alert">
          {removeMember.error.message}
        </p>
      )}

      {eligibleUsers.length > 0 && (
        <form onSubmit={handleAdd} className="mt-3 flex items-center gap-2">
          <select
            value={pickedUserId}
            onChange={(e) => setPickedUserId(e.target.value)}
            className="min-w-0 flex-1 rounded-lg border border-slate-300 px-2 py-1.5 text-xs text-slate-900 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
            aria-label={`Choisir un compte à ajouter à ${entityName}`}
          >
            <option value="">Choisir un compte...</option>
            {eligibleUsers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.display_name || u.email}
              </option>
            ))}
          </select>
          <button
            type="submit"
            disabled={!pickedUserId || addMember.isPending}
            className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-slate-800 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-slate-700 disabled:opacity-50"
          >
            <UserPlus size={12} aria-hidden />
            Ajouter
          </button>
        </form>
      )}

      {addMember.error && (
        <p className="mt-1 text-xs text-red-600" role="alert">
          {addMember.error.message}
        </p>
      )}
    </div>
  );
}
